// apps/dashboard/src/lib/consent.ts
import Cookies from 'js-cookie';
import { clientCookieAttributes } from './cookies';

/**
 * Analytics-consent gate for the event SDK (./analytics.ts) — deliberately
 * SEPARATE from marketing-email consent (`UserNotificationPreference.emailMarketing`
 * / the signup consent checkbox), per docs/analytics-data-platform.md §6: "The
 * event SDK respects an analytics-consent flag (separate from marketing-email
 * consent)."
 *
 * The founder chose OPT-IN on 2026-09-29: DEFAULT_POLICY below is 'required', so
 * nothing is collected until the visitor explicitly accepts. The UI for that
 * choice is components/consent/ConsentBanner.tsx (a slim bottom bar shown until
 * a choice is made), plus a "Pengaturan cookie" control (landing Footer,
 * dashboard Settings > Data & Privasi) to change the choice later. A browser
 * DNT/GPC signal still always denies, and in that case no banner is shown.
 *
 * Duplicated per B2C app (same convention as analytics.ts/anon-id.ts) — keep
 * the copies in sync until a shared client-safe package exists.
 */

export const ANALYTICS_CONSENT_COOKIE = 'aks_consent';
// Matches aks_anon's lifetime (see anon-id.ts) — no reason for the consent
// choice to expire sooner than the identifier it gates.
const ANALYTICS_CONSENT_MAX_AGE_DAYS = 400;

export type ConsentValue = 'granted' | 'denied';

/**
 * ── THE SINGLE SWITCH ─────────────────────────────────────────────────────
 * 'optional' → opt-out: collect unless the visitor (or their browser)
 *              explicitly says no. What shipped in PR #367.
 * 'required' → opt-in: collect ONLY after an explicit 'granted' choice is
 *              stored. Active since 2026-09-29 (founder decision). This
 *              constant is the ONLY place that decision lives.
 */
const DEFAULT_POLICY: 'optional' | 'required' = 'required';

/**
 * True if the browser itself is asserting a privacy signal — this always
 * overrides any stored consent value or the default policy, regardless of
 * DEFAULT_POLICY.
 */
export function hasBrowserDenialSignal(): boolean {
  if (typeof navigator === 'undefined') return false;

  // Do Not Track — '1' is the only value worth honouring; '0'/'unspecified'
  // are not a signal either way.
  if (navigator.doNotTrack === '1') return true;

  // Global Privacy Control (https://globalprivacycontrol.org/) — not yet in
  // TS's lib.dom.d.ts, hence the narrow cast instead of `any`.
  const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
  if (gpc === true) return true;

  return false;
}

/** The visitor's stored choice, or null if they have not made one (or the cookie is garbled). */
export function getStoredConsent(): ConsentValue | null {
  if (typeof document === 'undefined') return null;
  const raw = Cookies.get(ANALYTICS_CONSENT_COOKIE);
  return raw === 'granted' || raw === 'denied' ? raw : null;
}

/**
 * Resolves whether the event SDK may collect right now. Checked fresh on
 * every call (not cached) so a future consent UI can flip the stored cookie
 * mid-session and have the very next tracked event honour it. Precedence:
 *
 *   1. Browser denial signal (DNT / GPC) — always wins, no collection.
 *   2. An explicit stored choice — respected once something writes one.
 *   3. DEFAULT_POLICY — the fallback when neither of the above applies.
 */
export function hasAnalyticsConsent(): boolean {
  if (hasBrowserDenialSignal()) return false;

  const stored = getStoredConsent();
  if (stored) return stored === 'granted';

  return DEFAULT_POLICY === 'optional';
}

/** Fired on `window` whenever the stored choice changes; detail is the new value (null = cleared). */
export const CONSENT_CHANGE_EVENT = 'aks-consent-change';

export type ConsentChangeDetail = ConsentValue | null;

function emitConsentChange(value: ConsentChangeDetail): void {
  window.dispatchEvent(new CustomEvent<ConsentChangeDetail>(CONSENT_CHANGE_EVENT, { detail: value }));
}

/** Subscribes to consent changes (useSyncExternalStore-compatible). Returns the unsubscribe fn. */
export function subscribeToConsent(callback: () => void): () => void {
  window.addEventListener(CONSENT_CHANGE_EVENT, callback);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, callback);
}

/**
 * True when the visitor should be asked: opt-in policy, no stored choice, and no
 * browser DNT/GPC signal (that already denies, so asking would be pointless).
 */
export function shouldPromptForConsent(): boolean {
  if (DEFAULT_POLICY !== 'required') return false;
  if (hasBrowserDenialSignal()) return false;
  return getStoredConsent() === null;
}

/** Persists an explicit consent choice and notifies listeners (banner, PageViewTracker, ...). */
export function setAnalyticsConsent(value: ConsentValue): void {
  if (typeof document === 'undefined') return;
  Cookies.set(ANALYTICS_CONSENT_COOKIE, value, clientCookieAttributes(ANALYTICS_CONSENT_MAX_AGE_DAYS));
  emitConsentChange(value);
}

/**
 * Forgets the stored choice so the visitor is asked again (banner reappears). Under
 * the opt-in policy this also stops tracking immediately. Does not touch server-side data.
 */
export function clearAnalyticsConsent(): void {
  if (typeof document === 'undefined') return;
  const { path, domain } = clientCookieAttributes(ANALYTICS_CONSENT_MAX_AGE_DAYS);
  Cookies.remove(ANALYTICS_CONSENT_COOKIE, { path, ...(domain ? { domain } : {}) });
  emitConsentChange(null);
}
